import React, { useState } from 'react';
import {
  Box,
  Collapse,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Link } from 'react-router-dom';
import Image from '../../Image/Image';

const TICK_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/TickCircle.svg';
const CROSS_ICON_URL =
  'https://assets.askhippo.co.za/Icons/common/Delete-Square-New.svg';

const CELL_TEXT_SX = {
  color: '#162F3B',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 400,
  fontStyle: 'normal',
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  letterSpacing: '-0.002em',
  m: 0,
  width: '100%',
  wordBreak: 'break-word',
};

const DETAIL_LABEL_SX = {
  color: '#162F3B',
  fontFamily: 'Inter, sans-serif',
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  letterSpacing: '-0.002em',
  m: 0,
};

const renderCell = (cell, align = 'left') => {
  if (cell && typeof cell === 'object' && cell.icon) {
    return (
      <Image
        width={20}
        height={20}
        src={cell.icon === 'check' ? TICK_ICON_URL : CROSS_ICON_URL}
        alt={cell.icon === 'check' ? 'Yes' : 'No'}
        sx={{
          width: '20px',
          height: '20px',
          display: 'block',
          margin: align === 'center' ? '0 auto' : '0',
        }}
      />
    );
  }

  return (
    <Typography sx={{ ...CELL_TEXT_SX, textAlign: align }}>{cell}</Typography>
  );
};

const isIconCell = (cell) =>
  Boolean(
    cell &&
    typeof cell === 'object' &&
    typeof cell.icon === 'string' &&
    cell.icon
  );

const isLinkCell = (cell) =>
  Boolean(cell && typeof cell === 'object' && cell.url && 'text' in cell);

const MobileComparisonAccordionTable = ({
  table,
  defaultOpenIndex = 0,
  showHeaderTitle = true,
}) => {
  const theme = useTheme();
  const isCompactScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const headers = table?.headers || [];
  const rows = table?.rows || [];
  const tableTitle = showHeaderTitle ? headers?.[0] : '';
  const detailHeaders = headers.slice(1);
  const normalizedTableTitle = tableTitle
    ? String(tableTitle).trim().toLowerCase()
    : '';
  const initialOpenIndex =
    defaultOpenIndex === null || defaultOpenIndex === undefined
      ? null
      : rows.length > 0
        ? Math.min(defaultOpenIndex, rows.length - 1)
        : null;
  const [openIndex, setOpenIndex] = useState(initialOpenIndex);

  if (!Array.isArray(rows) || rows.length === 0) return null;

  const toggle = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  const normalizeComparableText = (value) =>
    String(value || '')
      .trim()
      .toLowerCase();

  const renderDetailRows = (detailCells, keyPrefix) => (
    <Box
      sx={{
        px: '16px',
        pb: '16px',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {detailHeaders.map((header, detailIndex) => {
        const cell = detailCells[detailIndex];
        if (cell === undefined || cell === null || cell === '') {
          return null;
        }

        const iconCell = isIconCell(cell);

        return (
          <Box
            key={`${keyPrefix}-${header || 'detail'}-${detailIndex}`}
            sx={{
              display: 'flex',
              alignItems: iconCell ? 'center' : 'flex-start',
              gap: '8px',
              py: iconCell ? '6px' : 0,
            }}
          >
            {iconCell ? (
              <>
                <Box
                  sx={{
                    flexShrink: 0,
                    minWidth: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  {renderCell(cell, 'left')}
                </Box>
                {header ? (
                  <Typography
                    sx={{
                      ...DETAIL_LABEL_SX,
                      flex: '1 1 auto',
                      minWidth: 0,
                    }}
                  >
                    {header}
                  </Typography>
                ) : null}
              </>
            ) : (
              <Box
                sx={{
                  display: isCompactScreen ? 'flex' : 'grid',
                  flexDirection: isCompactScreen ? 'column' : 'row',
                  alignItems: isCompactScreen ? 'flex-start' : 'start',
                  columnGap: isCompactScreen ? 0 : '8px',
                  rowGap: isCompactScreen ? '2px' : 0,
                  gridTemplateColumns: isCompactScreen
                    ? undefined
                    : '128px minmax(0, 1fr)',
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                {header ? (
                  <Typography
                    sx={{
                      ...DETAIL_LABEL_SX,
                      minWidth: 0,
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      width: isCompactScreen ? '100%' : 'auto',
                    }}
                  >
                    {header}
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'flex-start',
                    flex: '1 1 auto',
                    minWidth: 0,
                    width: isCompactScreen ? '100%' : 'auto',
                  }}
                >
                  {renderCell(cell, 'left')}
                </Box>
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: '24px',
        border: '1px solid #E3E6E8',
        overflow: 'hidden',
        backgroundColor: '#FFFFFF',
        boxSizing: 'border-box',
      }}
    >
      {tableTitle ? (
        <Box
          sx={{
            backgroundColor: '#C7CA00',
            px: '16px',
            py: '12px',
            borderBottom: '1px solid #BFC300',
            textAlign: 'center',
          }}
        >
          <Typography
            sx={{
              color: '#162F3B',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: '1rem',
              lineHeight: '1.25rem',
              letterSpacing: '-0.02em',
            }}
          >
            {tableTitle}
          </Typography>
        </Box>
      ) : null}

      {rows.map((row, index) => {
        const isOpen = openIndex === index;
        const rowCells = Array.isArray(row) ? row : [];
        const rowTitleCell = rowCells[0];
        const rowLinkUrl = isLinkCell(rowTitleCell) ? rowTitleCell.url : null;
        const rowTitle = isLinkCell(rowTitleCell)
          ? rowTitleCell.text
          : rowTitleCell;
        const detailCells = rowCells.slice(1);
        const hasDetails = detailHeaders.length > 0;
        const normalizedRowTitle = normalizeComparableText(rowTitle);
        const isTitleRow =
          normalizedTableTitle &&
          (normalizedRowTitle === normalizedTableTitle ||
            normalizedRowTitle.startsWith(`${normalizedTableTitle} `) ||
            normalizedRowTitle.startsWith(`${normalizedTableTitle}-`) ||
            normalizedRowTitle.includes(`${normalizedTableTitle} `));

        return (
          <Box
            key={`${rowTitle || 'row'}-${index}`}
            sx={{
              borderTop:
                index === 0 && !tableTitle ? 'none' : '1px solid #E3E6E8',
            }}
          >
            {isTitleRow ? (
              renderDetailRows(detailCells, `${rowTitle || 'row'}-${index}`)
            ) : (
              <>
                <Box
                  role="button"
                  tabIndex={0}
                  aria-expanded={isOpen}
                  onClick={() => toggle(index)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      toggle(index);
                    }
                  }}
                  sx={{
                    px: '16px',
                    py: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px',
                    cursor: 'pointer',
                    outline: 'none',
                    '&:focus-visible': {
                      boxShadow: 'inset 0 0 0 2px #A2ACB166',
                    },
                  }}
                >
                  <Typography
                    {...(rowLinkUrl
                      ? {
                          component: Link,
                          to: rowLinkUrl,
                          onClick: (event) => event.stopPropagation(),
                        }
                      : {})}
                    sx={{
                      color: '#162F3B',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: 700,
                      fontSize: '1rem',
                      lineHeight: '1.25rem',
                      letterSpacing: '-0.02em',
                      wordBreak: 'break-word',
                      ...(rowLinkUrl
                        ? {
                            textDecoration: 'none',
                            '&:hover': {
                              color: '#C7CA00',
                              textDecoration: 'underline',
                            },
                          }
                        : {}),
                    }}
                  >
                    {rowTitle}
                  </Typography>

                  {hasDetails ? (
                    <IconButton
                      disableRipple
                      tabIndex={-1}
                      aria-hidden="true"
                      sx={{
                        width: '28px',
                        height: '28px',
                        color: '#162F3B',
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 200ms ease',
                        flexShrink: 0,
                      }}
                    >
                      <ArrowDropDownIcon sx={{ fontSize: '28px' }} />
                    </IconButton>
                  ) : null}
                </Box>

                {hasDetails ? (
                  <Collapse in={isOpen} timeout={250}>
                    {renderDetailRows(
                      detailCells,
                      `${rowTitle || 'row'}-${index}`
                    )}
                  </Collapse>
                ) : null}
              </>
            )}
          </Box>
        );
      })}

      {table?.footer ? (
        <Box
          sx={{
            borderTop: '1px solid #E3E6E8',
            px: '16px',
            py: '12px',
          }}
        >
          <Typography
            sx={{
              color: '#162F3B',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 400,
              fontSize: '0.8125rem',
              lineHeight: '1.125rem',
              letterSpacing: '-0.02em',
              textAlign: 'center',
            }}
          >
            {table.footer?.replace(
              '{CURRENT_MONTH_YEAR}',
              new Date().toLocaleString('en-GB', {
                month: 'long',
                year: 'numeric',
              })
            )}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default MobileComparisonAccordionTable;
